import { useState, useEffect } from 'react';
import axios from 'axios';
import { ShoppingCart, Package, Search, Plus, Trash2, Check, Store, Truck, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const PRODUCT_PRESETS = [
  { name: 'Poster', defaultPrice: 0, hasSize: false },
  { name: 'Canvas', defaultPrice: 0, hasSize: true, sizes: ['24x18', '32x24', '40x30', '48x36'] },
  { name: 'Acrylic Print', defaultPrice: 0, hasSize: false },
  { name: 'Vyrus Shirt', defaultPrice: 0, hasSize: true, sizes: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'] },
  { name: 'Frame for Acrylic', defaultPrice: 0, hasSize: false },
  { name: 'Trading Card', defaultPrice: 0, hasSize: false },
];

// Target Wix Collection IDs for LSD inventory
const TARGET_COLLECTIONS = {
  lsd_collections: [
    '6bf7c880-a62b-9f30-1cf9-eda1bd710a86',  // Coloring Book
    '00648517-3b8a-5d4a-44e2-c206ef951cdd',  // Color With Vyrus
    '58047674-cc82-e376-e80e-ce1211940932',  // Entire Canvas Collections
    '531117c3-ee74-e804-92d5-bf42f860d0f6',  // LSD Canvases  
    '0318a0df-7972-c00d-f2db-30f91ede7312',  // Vyrus Canvases
    'b1e441bb-3e82-accc-2f87-58ffa88800c8',  // Entire Trading Card Products
    '6170acc9-4fb0-818b-38cf-0921809add43',  // Vyrus Trading Card Collection
    '6cb5ff72-f363-01b6-2ec9-f7b1b8b88260',  // Collaboration Trading Cards
    '3c4f22f9-c544-b307-d49d-d46d35b4fe11',  // Entire V Glass Acrylic Collection
    '1924461d-d510-cf02-6e3a-a9894956409a',  // V Glass (LSD) Acrylics
    'b40f0536-5fad-247a-4a97-f132fedaf143',  // V Glass (VYRUS) Acrylics
    '6beb4f5c-f99a-52b6-87d3-a53510d0a661',  // Entire Poster Print Collection
    '5c661e41-8ac3-9525-cd16-585197f9dd47',  // LSD Poster Prints
    '9823a990-3dbc-c616-b375-b2eb78a95b46',  // Vyrus Poster Prints
  ]
};

// Filter Wix products by collection IDs (same as Inventory page)
const filterWixProducts = (products, store) => {
  return products.filter(p => {
    const name = p.name.toLowerCase();
    
    // Exclude (WS) products
    if (name.includes('(ws)')) return false;
    
    // For LSD Collections - filter by collection membership
    if (store === 'lsd_collections') {
      const productCollections = p.collectionIds || [];
      const targetCollections = TARGET_COLLECTIONS.lsd_collections || [];
      return productCollections.some(cid => targetCollections.includes(cid));
    }
    
    // For Vyrus Clothing - include all shirts
    if (store === 'vyrus_clothing') {
      return true;
    }
    
    return false;
  });
};

export default function ConventionSales() {
  const [stores, setStores] = useState([]);
  const [events, setEvents] = useState([]);
  const [wixProducts, setWixProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [activeTab, setActiveTab] = useState('new'); // 'new' or 'history'
  const [filterEvent, setFilterEvent] = useState('');
  
  // Form state
  const [selectedStore, setSelectedStore] = useState('');
  const [selectedEvent, setSelectedEvent] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [shippingAddress, setShippingAddress] = useState('');
  const [shippingCity, setShippingCity] = useState('');
  const [shippingState, setShippingState] = useState('');
  const [shippingZip, setShippingZip] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ product_name: '', quantity: 1, price: '', catalog_item_id: null, design_name: '' }]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [activeItemIndex, setActiveItemIndex] = useState(null);
  const [pendingProduct, setPendingProduct] = useState(null); // For size selection
  const [showSizePicker, setShowSizePicker] = useState(false);

  // Canvas sizes for catalog items
  const CANVAS_SIZES = ['24x18', '32x24', '40x30', '48x36'];
  const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      fetchWixProducts(selectedStore);
    }
  }, [selectedStore]);

  const fetchInitialData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const [storesRes, eventsRes, salesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/wix/stores`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/events`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${BACKEND_URL}/api/convention-sales`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStores(storesRes.data.stores || []);
      setEvents(eventsRes.data || []);
      setSales(salesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWixProducts = async (store) => {
    setLoadingProducts(true);
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.get(`${BACKEND_URL}/api/wix/products/${store}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWixProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch Wix products:', error);
      setWixProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const addItem = () => {
    setItems([...items, { product_name: '', quantity: 1, price: '', catalog_item_id: null, design_name: '' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const selectProduct = (index, product, isWixProduct = false, selectedSize = null, fromStore = null) => {
    const name = product.name.toLowerCase();
    const needsSize = name.includes('canvas') || name.includes('shirt');
    
    // All Vyrus Clothing products are shirts - they all need sizes
    const isVyrusClothing = fromStore === 'vyrus_clothing' || selectedStore === 'vyrus_clothing';
    const productNeedsSize = needsSize || (isWixProduct && isVyrusClothing);
    
    // If it's a catalog item that needs size and no size selected yet, show size picker
    if (isWixProduct && productNeedsSize && !selectedSize) {
      setPendingProduct({ index, product, isWixProduct, isShirt: isVyrusClothing || name.includes('shirt') });
      setShowSizePicker(true);
      setShowProductPicker(false);
      return;
    }
    
    const newItems = [...items];
    let productName = product.name;
    
    // Append size to product name if selected
    if (selectedSize) {
      productName = `${product.name} - ${selectedSize}`;
    }
    
    newItems[index] = {
      ...newItems[index],
      product_name: productName,
      catalog_item_id: isWixProduct ? product.id : null,
      price: '', // Always manual price for conventions
      design_name: ''
    };
    setItems(newItems);
    setShowProductPicker(false);
    setShowSizePicker(false);
    setPendingProduct(null);
    setActiveItemIndex(null);
  };

  const selectSizeForPending = (size) => {
    if (pendingProduct) {
      selectProduct(pendingProduct.index, pendingProduct.product, pendingProduct.isWixProduct, size);
    }
  };

  const calculateTotal = () => {
    return items.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const qty = parseInt(item.quantity) || 1;
      return sum + (price * qty);
    }, 0);
  };

  const resetForm = () => {
    setCustomerName('');
    setCustomerEmail('');
    setCustomerPhone('');
    setShippingAddress('');
    setShippingCity('');
    setShippingState('');
    setShippingZip('');
    setNotes('');
    setItems([{ product_name: '', quantity: 1, price: '', catalog_item_id: null, design_name: '' }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!selectedStore) {
      toast.error('Please select a store');
      return;
    }
    if (!selectedEvent) {
      toast.error('Please select an event');
      return;
    }
    if (!customerName || !customerEmail || !customerPhone) {
      toast.error('Please fill in all customer information');
      return;
    }
    if (!shippingAddress || !shippingCity || !shippingState || !shippingZip) {
      toast.error('Please fill in complete shipping address');
      return;
    }
    
    const validItems = items.filter(item => item.product_name && item.price);
    if (validItems.length === 0) {
      toast.error('Please add at least one item with a price');
      return;
    }

    // Check if quick-select items have design names
    for (const item of validItems) {
      if (!item.catalog_item_id && !item.design_name) {
        const name = item.product_name.toLowerCase();
        if (name.includes('canvas') || name.includes('poster') || name.includes('acrylic')) {
          toast.error('Please enter a design name for: ' + item.product_name);
          return;
        }
      }
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('vyrus_token');
      const payload = {
        store: selectedStore,
        event_id: selectedEvent,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        shipping_address: shippingAddress,
        shipping_city: shippingCity,
        shipping_state: shippingState,
        shipping_zip: shippingZip,
        shipping_country: 'US',
        items: validItems.map(item => {
          // Append design name to product name if it exists
          let fullProductName = item.product_name;
          if (item.design_name) {
            fullProductName = `${item.product_name} - ${item.design_name}`;
          }
          return {
            product_name: fullProductName,
            quantity: parseInt(item.quantity) || 1,
            price: parseFloat(item.price),
            catalog_item_id: item.catalog_item_id
          };
        }),
        notes: notes || null
      };

      const response = await axios.post(`${BACKEND_URL}/api/wix/convention-sale`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(`Order created! Wix Order #${response.data.wix_order_number || response.data.wix_order_id}`);
      resetForm();
      fetchInitialData(); // Refresh sales list
      setActiveTab('history');
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create order in Wix');
    } finally {
      setSubmitting(false);
    }
  };

  const updateSaleStatus = async (saleId, status) => {
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.put(`${BACKEND_URL}/api/convention-sales/${saleId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status updated to ${status}`);
      fetchInitialData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteSale = async (saleId) => {
    if (!window.confirm('Delete this order? This will also cancel/remove it from Wix if possible.')) return;
    
    try {
      const token = localStorage.getItem('vyrus_token');
      const response = await axios.delete(`${BACKEND_URL}/api/convention-sales/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || 'Order deleted');
      fetchInitialData();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const filteredSales = filterEvent 
    ? sales.filter(s => s.event_id === filterEvent)
    : sales;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight">
          Convention Sales
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">Onsite convention-only, pay to ship orders (customers pay at the convention to get their item(s) shipped to them if it is sold out at the convention)</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        <Button
          variant={activeTab === 'new' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('new')}
          className="flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          New Order
        </Button>
        <Button
          variant={activeTab === 'history' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('history')}
          className="flex items-center gap-2"
        >
          <Package className="h-4 w-4" />
          Order History ({sales.length})
        </Button>
      </div>

      {activeTab === 'new' ? (
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Store & Event Selection */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Store className="h-5 w-5 text-primary" />
                Store & Event
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Wix Store *</label>
                  <select
                    value={selectedStore}
                    onChange={(e) => setSelectedStore(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                    required
                  >
                    <option value="">Select Store</option>
                    {stores.map(store => (
                      <option key={store.id} value={store.id}>{store.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Convention/Event *</label>
                  <select
                    value={selectedEvent}
                    onChange={(e) => setSelectedEvent(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                    required
                  >
                    <option value="">Select Event</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>{event.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Customer Information */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Customer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Full Name *</label>
                  <Input
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="John Doe"
                    required
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Email *</label>
                  <Input
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="john@example.com"
                    required
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Phone *</label>
                  <Input
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="555-123-4567"
                    required
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipping Address */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Truck className="h-5 w-5 text-primary" />
                Shipping Address
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm text-muted-foreground mb-1 block">Street Address *</label>
                <Input
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  placeholder="123 Main St, Apt 4B"
                  required
                  className="bg-secondary/50 border-transparent focus:border-primary"
                />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <label className="text-sm text-muted-foreground mb-1 block">City *</label>
                  <Input
                    value={shippingCity}
                    onChange={(e) => setShippingCity(e.target.value)}
                    placeholder="New York"
                    required
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">State *</label>
                  <select
                    value={shippingState}
                    onChange={(e) => setShippingState(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                    required
                  >
                    <option value="">State</option>
                    {US_STATES.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">ZIP *</label>
                  <Input
                    value={shippingZip}
                    onChange={(e) => setShippingZip(e.target.value)}
                    placeholder="10001"
                    required
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Products */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5 text-primary" />
                  Products
                </span>
                <Button type="button" variant="outline" size="sm" onClick={addItem}>
                  <Plus className="h-4 w-4 mr-1" /> Add Item
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {items.map((item, index) => (
                <div key={index} className="flex flex-col gap-3 p-3 bg-secondary/30 rounded-lg">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <label className="text-xs text-muted-foreground mb-1 block">Product</label>
                      <div className="flex gap-2">
                        <Input
                          value={item.product_name}
                          onChange={(e) => updateItem(index, 'product_name', e.target.value)}
                          placeholder="Product name"
                          className="bg-secondary/50 border-transparent focus:border-primary"
                        />
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setActiveItemIndex(index);
                            setShowProductPicker(true);
                          }}
                        >
                          <Search className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="w-20">
                      <label className="text-xs text-muted-foreground mb-1 block">Qty</label>
                      <Input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="bg-secondary/50 border-transparent focus:border-primary"
                      />
                    </div>
                    <div className="w-28">
                      <label className="text-xs text-muted-foreground mb-1 block">Price *</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.price}
                          onChange={(e) => updateItem(index, 'price', e.target.value)}
                          placeholder="0.00"
                          className="pl-7 bg-secondary/50 border-transparent focus:border-primary"
                        />
                      </div>
                    </div>
                    {items.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(index)}
                        className="self-end text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {/* Design name field - shows for quick select items that need it */}
                  {!item.catalog_item_id && item.product_name && (
                    item.product_name.toLowerCase().includes('canvas') ||
                    item.product_name.toLowerCase().includes('poster') ||
                    item.product_name.toLowerCase().includes('acrylic') ||
                    item.product_name.toLowerCase().includes('shirt')
                  ) && (
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Design Name *</label>
                      <Input
                        value={item.design_name || ''}
                        onChange={(e) => updateItem(index, 'design_name', e.target.value)}
                        placeholder="e.g., Toga Waifu, Gear 5 Luffy, etc."
                        className="bg-secondary/50 border-transparent focus:border-primary"
                      />
                    </div>
                  )}
                </div>
              ))}

              {/* Total */}
              <div className="flex justify-end pt-4 border-t border-border">
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Order Total</p>
                  <p className="text-2xl font-bold text-primary font-mono">
                    {formatCurrency(calculateTotal())}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Order Notes (Optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any special instructions or notes..."
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground resize-none"
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <Button
            type="submit"
            disabled={submitting}
            className="w-full py-6 text-lg bg-primary hover:bg-primary/90"
          >
            {submitting ? (
              'Creating Order in Wix...'
            ) : (
              <>
                <Check className="h-5 w-5 mr-2" />
                Create Order & Sync to Wix
              </>
            )}
          </Button>
        </form>
      ) : (
        /* Order History Tab */
        <div className="space-y-4">
          {/* Filter */}
          <div className="flex gap-3">
            <select
              value={filterEvent}
              onChange={(e) => setFilterEvent(e.target.value)}
              className="px-4 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
            >
              <option value="">All Events</option>
              {events.map(event => (
                <option key={event.id} value={event.id}>{event.name}</option>
              ))}
            </select>
          </div>

          {/* Sales List */}
          {filteredSales.length === 0 ? (
            <Card className="p-8 text-center bg-card border-border/50">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No convention sales yet</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredSales.map((sale) => (
                <Card key={sale.id} className="bg-card border-border/50">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{sale.customer_name}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            sale.status === 'shipped' ? 'bg-blue-500/20 text-blue-400' :
                            sale.status === 'delivered' ? 'bg-green-500/20 text-green-400' :
                            sale.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {sale.status?.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{sale.customer_email}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {sale.event_name} • {sale.store === 'lsd_collections' ? 'LSD Collections' : 'Vyrus Clothing'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sale.items?.length || 0} item(s) • {new Date(sale.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-lg font-bold text-primary font-mono">
                          {formatCurrency(sale.total)}
                        </p>
                        {sale.status === 'pending_shipment' && (
                          <Button
                            size="sm"
                            onClick={() => updateSaleStatus(sale.id, 'shipped')}
                            className="bg-blue-500 hover:bg-blue-600"
                          >
                            Mark Shipped
                          </Button>
                        )}
                        {sale.status === 'shipped' && (
                          <Button
                            size="sm"
                            onClick={() => updateSaleStatus(sale.id, 'delivered')}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            Mark Delivered
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-destructive hover:bg-destructive/10"
                          onClick={() => deleteSale(sale.id)}
                          data-testid={`delete-sale-${sale.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    {sale.wix_order_number && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Wix Order: #{sale.wix_order_number || sale.wix_order_id?.slice(0, 8)}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Product Picker Modal */}
      {showProductPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[80vh] overflow-hidden bg-card">
            <CardHeader className="border-b border-border flex-row items-center justify-between">
              <CardTitle>Select Product</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowProductPicker(false)}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-4 overflow-y-auto max-h-[60vh]">
              {/* Preset Products */}
              <p className="text-sm font-medium text-muted-foreground mb-2">Quick Select</p>
              <div className="space-y-2 mb-4">
                {PRODUCT_PRESETS.map((product) => (
                  <div key={product.name} className="space-y-1">
                    {product.hasSize ? (
                      <div className="border border-border rounded-lg p-2">
                        <p className="text-sm font-medium mb-2">{product.name}</p>
                        <div className="flex flex-wrap gap-1">
                          {product.sizes.map((size) => (
                            <Button
                              key={size}
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => selectProduct(activeItemIndex, product, false, size)}
                            >
                              {size}
                            </Button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full justify-start"
                        onClick={() => selectProduct(activeItemIndex, product, false)}
                      >
                        {product.name}
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Wix Catalog */}
              {selectedStore && (
                <>
                  <p className="text-sm font-medium text-muted-foreground mb-2 mt-4">
                    From Wix Catalog {loadingProducts && '(Loading...)'}
                  </p>
                  {filterWixProducts(wixProducts, selectedStore).length > 0 ? (
                    <div className="space-y-2">
                      {filterWixProducts(wixProducts, selectedStore).map((product) => (
                        <Button
                          key={product.id}
                          variant="outline"
                          className="w-full justify-start"
                          onClick={() => selectProduct(activeItemIndex, product, true)}
                        >
                          <div className="flex items-center gap-3 w-full">
                            {product.media && (
                              <img 
                                src={product.media} 
                                alt={product.name} 
                                className="w-8 h-8 object-cover rounded"
                              />
                            )}
                            <span className="flex-1 text-left truncate">{product.name}</span>
                          </div>
                        </Button>
                      ))}
                    </div>
                  ) : !loadingProducts && (
                    <p className="text-sm text-muted-foreground">No products found in catalog</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Size Picker Modal for Catalog Items */}
      {showSizePicker && pendingProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-sm bg-card">
            <CardHeader className="border-b border-border flex-row items-center justify-between">
              <CardTitle className="text-base">Select Size</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowSizePicker(false); setPendingProduct(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-3">{pendingProduct.product.name}</p>
              {pendingProduct.isShirt || selectedStore === 'vyrus_clothing' ? (
                <div className="grid grid-cols-3 gap-2">
                  {SHIRT_SIZES.map((size) => (
                    <Button
                      key={size}
                      variant="outline"
                      size="sm"
                      onClick={() => selectSizeForPending(size)}
                    >
                      {size}
                    </Button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  {CANVAS_SIZES.map((size) => (
                    <Button
                      key={size}
                      variant="outline"
                      onClick={() => selectSizeForPending(size)}
                    >
                      {size}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
